import { rgbaToHexA, rotation_to_flat } from '../../../functions';
import { Buildings, isDesktop, isMobile, isTablet, settingsData, threeJsVariables, UIRoot } from '../../../settings';
import { intersectionClickHandler, selectionBoxesSetStatusColor } from '../../threejs/raycast';
import { changeLangToCurrent, getCurrentLang, getCurrentLangDirection } from '../langSwitcher/langSwitcher';
import gsap from 'gsap';
import { UnitCardClass } from '../../cards/unitCards/UnitCardClass';
import { MathUtils, Object3D } from 'three';
import { CSS2DObject } from '../../../libs/CSS2DRenderer';
import {Cloudflare} from "../../api/Cloudflare";
import {BaseElement} from "../../interfaces/BaseElement";
import { Button } from '../../interfaces/buttons/Button';
import { DreamsPreloader } from '../preloader/DreamsPreloader';
import { PhotoSwiperGallery } from '../../menu/gallery/galleryDesktop';
import Swiper from "swiper";
import {Navigation} from "swiper/modules";

export class Amenities {
    static parentContainer;
    static amenitiesContainer;
    static amenitiesSwiper;
    static amenitiesSwiperElement;
    static amenitiesWrapper;
    static amenitiesArray = [];

    constructor (building) {
        this.amenities = building.customSelections.concat(building.cylinderLabels);
        this.amenities.sort((prev,next) => {
           const prevIndex = config.features.amenities.findIndex(el => el?.engineId === prev.name);
           const nextIndex = config.features.amenities.findIndex(el => el?.engineId === next.name);
           if (prevIndex > nextIndex) {
               return 1;
           } else if (prevIndex < nextIndex) {
               return -1;
           } else {
               return 0;
           }
        });
    }

    add() {
        if (!this.amenities.length) {
            Amenities.clear();
            return;
        }

        Amenities.clear();
        this.amenities.forEach(amenity =>{
            let amenityElement;
            if (amenity.type === 'CylinderCSSLabel') {
                amenityElement = new CylinderCSSLabel(Amenities.amenitiesWrapper).create(amenity)
            } else {
                amenityElement = new Amenity(Amenities.amenitiesWrapper).create(amenity);
            }
            if (!isMobile)
                amenityElement.element.addClass('swiper-slide')
            Amenities.amenitiesArray.push(amenityElement);
        });

        if (!isMobile) {
            new BaseElement()
                .addClass('amenities__navigation-btn', 'swiper-button-next', 'icon-down2')
                .appendTo(Amenities.amenitiesSwiperElement.DOMElement);
            new BaseElement()
                .addClass('amenities__navigation-btn', 'swiper-button-prev', 'icon-up2')
                .appendTo(Amenities.amenitiesSwiperElement.DOMElement);

            Amenities.destroySwiper();
            Amenities.createSwiper();
        }
    }

    static createMainContainer(){
        if (isMobile)
            return;

        const checkAmenities = Buildings.getBuildingsArray().some(building => building.customSelections.length || building.cylinderLabels.length);
        if (!checkAmenities)
            return;

        this.parentContainer = UIRoot;

        if (!this.exist()) {
            Amenities.amenitiesContainer = new BaseElement()
                .addClass('amenities__main__container', 'non-canvas', 'swiper')
                .appendTo(this.parentContainer);
            Amenities.amenitiesSwiperElement = new BaseElement()
                .addClass('amenities__swiper')
                .appendTo(Amenities.amenitiesContainer.DOMElement);
            Amenities.amenitiesWrapper = new BaseElement()
                .addClass('amenities-wrapper', 'swiper-wrapper')
                .appendTo(Amenities.amenitiesSwiperElement.DOMElement);
            if (isTablet)
                Amenities.amenitiesContainer.addClass('tablet-styles');
        } else {
            Amenities.show();
        }
    }

    static destroySwiper() {
        if (Amenities.amenitiesSwiper) {
            Amenities.amenitiesSwiper.destroy();
            Amenities.amenitiesSwiper = null;
        }
    }

    static createSwiper() {
        if (!Amenities.exist())
            return;

        Swiper.use([Navigation]);
        Amenities.amenitiesSwiper = new Swiper('.amenities__swiper', {
            direction: 'vertical',
            slidesPerView: 'auto',
            navigation: {
                nextEl: ".amenities__navigation-btn.swiper-button-next",
                prevEl: ".amenities__navigation-btn.swiper-button-prev",
            }
        });
    }

    static hide () {
        if (!this.exist())
            return;

        if (Amenities.amenitiesContainer?.DOMElement?.classList.contains('hidden'))
            return;

        Amenities.amenitiesArray.forEach(amenity => amenity.hoverOut());

        gsap.to(Amenities.amenitiesContainer.DOMElement,{
            scale:0,
            duration: .3,
            onComplete: () => {
                Amenities.amenitiesContainer.addClass('hidden');
                Amenities.amenitiesContainer.removeStyle('transform');
            }
        });
    }

    static show(delay = .8) {
        if (!this.exist() || UnitCardClass.findCard()?.html || !Amenities.amenitiesContainer?.DOMElement?.classList.contains('hidden') || isMobile)
            return;

        gsap.fromTo(Amenities.amenitiesContainer.DOMElement, {
            scale: 0
        }, {
            scale: 1,
            delay: delay,
            duration: .5,
            onStart: () => {
                Amenities.amenitiesContainer.removeClass('hidden');
            },
            onComplete: () => {
                Amenities.amenitiesContainer.removeStyle('transform');
                Amenities.createSwiper();
            }
        });
    }

    static hideAmenitiesSelectionBoxes() {
        if (config.features?.keyplanAmenities)
            return;

        settingsData.objects_to_intersection = settingsData.objects_to_intersection.filter(object =>
            !settingsData.customSelections.includes(object)
        );
    }

    static showAmenitiesSelectionBoxes() {
        if (config.features?.keyplanAmenities)
            return;

        settingsData.objects_to_intersection = settingsData.objects_to_intersection.concat(settingsData.customSelections.filter(selection => selection?.isObject3D));
    }

    static clear(){
        if (isMobile)
            Amenities.amenitiesArray = [];

        if (!this.exist())
            return;

        while (Amenities.amenitiesWrapper?.DOMElement?.firstElementChild) {
            Amenities.amenitiesWrapper.DOMElement.firstElementChild.remove();
        }
        this.amenitiesArray = [];
    }

    static unselectAmenities() {
        this.exist() && this.amenitiesArray.forEach(amenity => amenity.hoverOut());
    }

    static exist(){
        // return Amenities.amenitiesContainer?.DOMElement || document.querySelector('.amenities__main__container');
        return Amenities.amenitiesContainer?.DOMElement;
    }
}

class Amenity {
    constructor (container) {
        this.container = container;
        this.amenity = null;
        this.element = null;
        this.crmData = null;
        this.highlightColor = rgbaToHexA(config.style.colors.find(color => color.key === 'brand2').value.split(','),false).substring(1);
        this.active = false;
        this.name = null;
        this.direction = getCurrentLangDirection();
        this.amenityPoint = new BaseElement().addClass('amenity__mesh__point','hide__point');
        new BaseElement('span').addClass('amenity__mesh__span').appendTo(this.amenityPoint.DOMElement);
        this.amenityLabel = new CSS2DObject(this.amenityPoint.DOMElement);
        this.amenityLabel.userData.zoom = 1;
        this.amenityLabel.name = `amenity`;
        this.line = null;
    }

    create(amenity) {
        this.amenity = amenity;
        this.hasEngineID = this.amenity.userData.crm_data.engineId !== '';
        if (typeof this.amenity.add === 'function')
            this.amenity.add(this.amenityLabel);
        this.crmData = amenity.userData.crm_data;
        if (!this.crmData) {
            console.log(`%cData for ${this.amenity.name} is absent`, 'color: orange');
            return;
        }

        // const parseName = this.crmData.propNum.match(/([A-Z]|([a-z]))/g).join('').toLowerCase();
        // this.name = parseName.charAt(0).toUpperCase() + parseName.substring(1);
        this.name = this.crmData.propNum || this.crmData.title;
        const imgUrl = Boolean(this.crmData.assets.preview2D) ? this.crmData.assets.preview2D :
            Array.isArray(this.crmData.assets.gallery) ? this.crmData.assets.gallery[0] : undefined;

        this.element = new BaseElement()
          .addClass('amenity__btn')
          .addAttribute('amenity', this.name);
        this.elementForCard = new BaseElement(this.element.DOMElement.cloneNode(true));

        if (this.container?.DOMElement)
            this.element.appendTo(this.container.DOMElement);

        if (imgUrl) {
            this.element.addStyle('background-image', `url("${Cloudflare.resize(imgUrl, {height: 256})}")`);

            const imageContainer = new BaseElement()
              .addClass('amenity__image-container')
              .appendTo(this.elementForCard.DOMElement);
            new BaseElement('img')
              .addClass('amenity__image')
              .setAttributes({
                  'src': `${Cloudflare.resize(imgUrl, {height: 256})}`,
                  'alt': 'Amenity image'
              })
              .appendTo(imageContainer.DOMElement);
        }

        if (isMobile || isDesktop || isTablet) {
            this.element.event('click', () => {
                if (isMobile || isTablet) {
                    this.click();
                } else {
                    Amenities.unselectAmenities();
                    if (this.element.DOMElement.classList.contains('active__amenity')) {
                        this.hoverOut();
                    } else {
                        this.hover();
                    }
                    this.click();
                }
            });
        } else {
            this.element
                .event('mouseenter', (e) => this.hover(e))
                .event('mouseleave', () => this.hoverOut())
                .event('click', () => this.click());
        }

        const text = new BaseElement('span')
            .addClass('amenity__text', 'language-string')
            .addAttribute('data-dictionary', this.name)
            .innerHtml(changeLangToCurrent(this.name))
            .appendTo(this.element.DOMElement);


        const infoWrapper = new BaseElement()
            .addClass('amenity__info-wrapper')
            .appendTo(this.elementForCard.DOMElement);
        new BaseElement(text.DOMElement.cloneNode(true)).appendTo(infoWrapper.DOMElement);
        new BaseElement('span')
            .addClass('amenity_icon', 'icon-lotus')
            .appendTo(infoWrapper.DOMElement);


        if (!isMobile && config.resources.background.cylinderBg === 'sky')
            text.addClass('dark-text');

        return this;
    }

    hover() {
        if (this.amenity.userData.crm_data.showTarget === false) {
            this.element.addClass('active__amenity');
            this.active = true;
            return;
        }
        this.element.addClass('active__amenity');
        this.active = true;
        if (this.hasEngineID) {
            rotation_to_flat(this.amenity);
            selectionBoxesSetStatusColor(this.amenity,'amenityHover',false,this.highlightColor);
        }

        if (!isMobile && this.hasEngineID){
            this.drawLine();
            this.updateLine();
        }
    }

    drawLine(){
        if (this.line)
            return;

        // this.line = createHtmlElement(document.body,'','div',['amenity__line']);
        this.line = new BaseElement()
          .addClass('amenity__line')
          .appendTo(document.body);
        const bounding = this.element.DOMElement.getBoundingClientRect();
        this.direction = getCurrentLangDirection();

        if (this.direction === 'rtl' && !isDesktop) {
            this.line.addStyle('left', bounding.left + bounding.width + 'px');
        } else {
            this.line.addStyle('left', bounding.left + 'px');
        }
        this.line.addStyle('top', bounding.bottom + 'px');
    }

    updateLine() {
        if (this.active){
            requestAnimationFrame(this.updateLine.bind(this));
            const {bottom,left,width} = this.element.DOMElement.getBoundingClientRect();
            const elementWidth = this.direction === 'rtl' && !isDesktop ? width : 0;
            const amenity2D = this.amenityPoint.DOMElement.getBoundingClientRect();
            const distanceX = Math.abs(left - amenity2D.left) - elementWidth;
            const distanceY = Math.abs(bottom - amenity2D.bottom);
            const square = Math.sqrt((distanceX * distanceX) + (distanceY * distanceY));
            const angle = Math.atan2(amenity2D.bottom - bottom,amenity2D.left - left - elementWidth);
            const degree = MathUtils.radToDeg(angle);
            this.line
              .addStyle('transform', `rotateZ(${degree}deg)`)
              .addStyle('width', `${square}px`);
        }
    }

    hoverOut(){
        if (this.amenity.userData.crm_data.showTarget === false) {
            this.active = false;
            this.element.removeClass('active__amenity');
            return;
        }
        this.active = false;
        this.element.removeClass('active__amenity');

        if (this.hasEngineID)
            selectionBoxesSetStatusColor(this.amenity,'hoverOut',false);

        if (this.line){
            this.line.remove();
            this.line = null;
        }
    }

    click(){
        intersectionClickHandler(this.amenity);
    }
}


class CylinderCSSLabel extends Amenity{
    constructor (container) {
        super(container);
        
    }
    
    create (label) {
        this.amenity = new Object3D();
        this.name = label.data.title.en || Date.now();
        this.amenity.position.fromArray(label.position);
        this.amenity.name = this.name;
        this.amenity.add(this.amenityLabel);
        this.labelData = label;

        if (!this.labelData.data.hasOwnProperty('event')) {
            this.labelData.data.event = true;
        }

        this.url = new URL(label.data.url);
        this.type = this.url.pathname.match(/\.(htm|html)/g) ? '360' : 'img';
        this.gallery = label.data?.gallery && label.data?.gallery.length > 0 ? label.data.gallery : null;
    
        this.element = new BaseElement()
            .addClass('amenity__btn')
            .addAttribute('amenity', this.name)
            .appendTo(this.container.DOMElement);
        
        if (this.type === 'img') {
            this.element.addStyle('background-image', `url("${Cloudflare.resize(this.url.href, {height: 256})}")`);
        } else {
            if (this.labelData.data.preview360) {
                this.element.addStyle('background-image', `url("${Cloudflare.resize(this.labelData.data.preview360, {height: 256})}")`);
            } else {
                this.element.addClass('icon-360','amenity__tour-btn');
            }
        }
    
        if (isMobile || isDesktop || isTablet) {
            this.element.event('click', () => {
                if (isMobile || isTablet) {
                    this.click();
                } else {
                    Amenities.unselectAmenities();
                    if (this.element.DOMElement.classList.contains('active__amenity')) {
                        this.hoverOut();
                    } else {
                        this.hover();
                    }
                }
            });
        } else {
            this.element.event('mouseenter', (e) => this.hover(e));
            this.element.event('mouseleave', () => this.hoverOut());
            this.element.event('click', () => this.click());
        }
        
        const text = new BaseElement('span')
            .addClass('amenity__text', 'language-string')
            .appendTo(this.element.DOMElement);
        
        if (config.dictionary[this.name]) {
            text.addAttribute('data-dictionary',this.name);
            text.innerHtml(changeLangToCurrent(this.name));
        } else {
            Object.entries(this.labelData.data.title).forEach(([lang,value]) => {
               text.addAttribute(`data-lang-${lang}`,value);
            });
            const translation = this.labelData.data.title[getCurrentLang()] || '';
            text.innerHtml(translation);
        }
        
        if (config.resources.background.cylinderBg === 'sky')
            text.addClass('dark-text');
        
        return this;
    }
    
    hover() {
        if (this.labelData.data.rotate) {
            if (this.amenity.parent === null) {
                threeJsVariables.scene.add(this.amenity);
            }
            this.element.addClass('active__amenity');
            this.active = true;
            rotation_to_flat(this.amenity);
        } else {
            this.element.addClass('active__amenity');
            this.active = true;
            return;
        }

        if (!isMobile){
            this.drawLine();
            this.updateLine();
        }
    }
    
    hoverOut(){
        if (this.labelData.data.rotate) {
            this.active = false;
            this.element.removeClass('active__amenity');
            if (this.line){
                this.line.remove();
                this.line = null;
            }
            if (this.amenity.parent) {
                this.amenity.removeFromParent();
            }
            
        } else {
            this.active = false;
            this.element.removeClass('active__amenity');
        }
    }
    
    click () {
        if (!this.labelData.data.event)
            return;

        settingsData.stopRender = true;
        this.mainContainer = new BaseElement()
            .addClass('orbit-360-viewer')
            .appendTo(document.body);
        const content = new BaseElement()
            .addClass('orbit-360-viewer__content')
            .appendTo(this.mainContainer.DOMElement);
    
        let viewer;
    
        if (this.type === '360') {
            new Button()
                .addClass('icon-close')
                .event('click', this.closeHandler.bind(this))
                .appendTo(content.DOMElement);
            this.preloader = new BaseElement()
                .addClass('orbit-360-preloader')
                .appendTo(content.DOMElement);
            new DreamsPreloader(this.preloader.DOMElement,'orbit-360-pr').show();
            viewer = new BaseElement('iframe')
                .addClass('orbit-360-viewer__player','player-iframe')
                .addAttribute('src',this.url.href)
                .appendTo(content.DOMElement);
            viewer.DOMElement.onload = () => this.preloader.remove();
        } else {
            viewer = new BaseElement('div')
                .addClass('orbit-360-viewer__player','player-img')
                .appendTo(content.DOMElement);
        
            let data = this.gallery && this.gallery.length ? this.gallery : [this.url.href];
            data = data.map(url => Cloudflare.resize(url,{width: isMobile ? 1024 : 2880}));
            const gallery = new PhotoSwiperGallery(data,0,viewer.DOMElement);
            gallery.onClose = () => this.closeHandler();
            gallery.open();
        }
    }
    
    closeHandler() {
        this.mainContainer.remove();
        settingsData.stopRender = false;
    }
    
}
